import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from "react";
import Numpad from "@/components/pos/Numpad";

const NumpadContext = createContext(null);

/**
 * Global numpad provider.
 * Any page can call useNumpad().open({ value, label, onChange, onApply, sticky })
 * and the numpad will appear fixed at the bottom-right of the screen.
 */
export function NumpadProvider({ children }) {
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState("");
    const [label, setLabel] = useState("");
    const handlersRef = useRef({});

    const openNumpad = useCallback((opts = {}) => {
        const { value: v = "", label: l = "ΕΙΣΑΓΩΓΗ", onChange, onApply } = opts;
        setValue(v);
        setLabel(l);
        handlersRef.current = { onChange, onApply };
        setOpen(true);
    }, []);

    const closeNumpad = useCallback(() => {
        setOpen(false);
        handlersRef.current = {};
    }, []);

    const setNumpadValue = useCallback((v) => {
        setValue(v);
        handlersRef.current.onChange?.(v);
    }, []);

    const press = useCallback(
        (k) => {
            const current = value || "";
            if (k === "←") {
                setNumpadValue(current.slice(0, -1));
                return;
            }
            if (k === ",") {
                if (current.includes(",")) return;
                setNumpadValue((current || "0") + ",");
                return;
            }
            setNumpadValue(current + k);
        },
        [value, setNumpadValue]
    );

    const apply = useCallback(() => {
        const onApply = handlersRef.current.onApply;
        if (onApply) onApply(value);
        // close after apply for a clean UX (page can re-open on next focus)
        setOpen(false);
    }, [value]);

    const ctx = useMemo(
        () => ({
            isOpen: open,
            open: openNumpad,
            close: closeNumpad,
            setValue: setNumpadValue,
            toggle: (opts) => (open ? closeNumpad() : openNumpad(opts)),
        }),
        [open, openNumpad, closeNumpad, setNumpadValue]
    );

    return (
        <NumpadContext.Provider value={ctx}>
            {children}
            {open && (
                <Numpad
                    value={value}
                    targetLabel={label}
                    onPress={press}
                    onClear={() => setNumpadValue("")}
                    onApply={apply}
                    onClose={closeNumpad}
                />
            )}
        </NumpadContext.Provider>
    );
}

export function useNumpad() {
    const ctx = useContext(NumpadContext);
    if (!ctx) {
        throw new Error("useNumpad must be used inside <NumpadProvider>");
    }
    return ctx;
}
